/*
 * Tickets (wallet) — V1.9.1 measured design (handoff README §Screens →
 * 3. Tickets; App Shell.dc.html's wallet view is the measured DOM reference).
 *
 * The ticket is a CLASSIC STUB: organiser banner (logo + accent + hero — ALL
 * data-driven per org unit from settings' resolved branding read,
 * src/data/orgBranding.ts), ADMIT ONE strip, participant name LARGEST, event /
 * type / heat / date (+ gate only when the event has gates), a perforation,
 * then ONE QR at a time with prev/next pagers (two QRs on screen invites the
 * gate operator to scan the wrong one). Pager glyphs swap in RTL — in code,
 * never CSS-mirrored. Serial / booking code renders as a mono LTR island.
 *
 * BRANDING (BOARDROOM-2026-08-28-110128): the org-unit id is the event's
 * organiser.id ONLY when it is a real UUID (isOrgUnitUuid — the endpoint 400s
 * on synthesised ids). Today's wire emits none, so the banner renders the
 * honest NEUTRAL fallback: no logo, neutral band, no hero — never a fabricated
 * asset, never a letter monogram (PRODUCT-RULES §Logos).
 *
 * DATA (Rulings A + C, BOARDROOM-2026-08-09-133534):
 *  - SHIPPED wallet renders ONLY live data — the real events the signed-in
 *    participant holds a place in, via the SAME source Get ready consumes
 *    (useLiveGetReadyEvents → server-derived readiness steps). Honest empty
 *    state when there are none. The canon 5-event [demo] dataset
 *    (src/data/seed.ts) is a TEST FIXTURE only and is never shipped.
 *  - Issuance is DERIVED: "a ticket is 'issued' only when every step is done."
 *    The pager (issued) / NOT-ISSUED-YET (pending) split reuses isIssued /
 *    nowStepCount from src/data/getReady.ts — no stored issued flag, and a
 *    ticket a user is hunting for is never simply absent.
 *
 * QR (must-not: the DC's seeded-random genQR / badge-geo three.js never ship):
 * the QR is the REAL scannable payload — the ticket's STABLE bare-UUID scan
 * token (tickets.anonymous_scan_token) fetched from my-api in ONE authed GET
 * (BOARDROOM-2026-08-24-192147 GO), rendered with the `qrcode` lib at ECC 'Q'.
 * The read is server-gated on get-ready readiness (409 not_ready), so no token
 * ⇒ an honest not-ready state, never a fabricated code.
 *
 * WALLET PASSES: Apple/Google pass generation is NOT built server-side — no
 * pass buttons render at all (README §Screens 3: prefer absent over honestly-
 * disabled). The in-app QR IS the pass.
 */
import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { LiveDataNotice, useData } from '../data/DataProvider'
import { NotWired } from '../components/NotWired'
import { useShellChrome } from '../components/Shell'
import { useLocale, type TranslateFn, translate } from '../i18n/useLocale'
import { loadSession } from '../auth/session'
import { startSignIn } from '../auth/startSignIn'
import { formatBib } from '../data/bib'
import { formatEventDate } from './HeatSelect'
import { fetchPurchaserScanToken, fetchScanToken, myApiBase, MyApiError } from '../data/myApi'
import { isOrgUnitUuid, type OrgBrandingResolved } from '../data/orgBranding'
import {
  eventEnded, isIssued, nowStepCount, visibleGetReadyEvents,
  type GetReadyEvent, type GetReadyOrganiser,
} from '../data/getReady'
import { useLiveGetReadyEvents } from './GetReady'

// ---------------------------------------------------------------------------
// Wallet event shape — GetReadyEvent (the single source of truth) + stub
// display fields
// ---------------------------------------------------------------------------

/** Wallet-stub display fields. Unknown values render an honest em-dash, never
 *  an invention. */
export interface WalletStubFields {
  participantName: string
  ticketType: string
  /** Heat START TIME only, e.g. "07:30" (the label is HEAT, not Wave, and the
   *  value is the start time, never the wave name). */
  heat: string
  /** Empty/absent ⇒ the Gate column is OMITTED (README §Screens 3: gate only
   *  when the event has gates — no gate data on today's wire ⇒ no gate row). */
  gate: string
  date: string
  /** Minted bib for display, already formatted ("001"). The Bib column is
   *  ALWAYS present (BOARDROOM-2026-08-13-223623 — the field must exist even
   *  before allocation); renders '—' until box-office allocates it. */
  bib: string
  /** Serial / booking code — mono LTR island under the QR (README §Screens 3).
   *  The REAL order reference; absent ⇒ the line is omitted, never invented. */
  serial?: string
}

export interface WalletEvent extends GetReadyEvent {
  organiser: GetReadyOrganiser & {
    /** FIXTURE-ONLY accent seam (seed.ts [demo] values). The live path never
     *  sets this — production banner theming comes exclusively from the
     *  resolved org branding below. */
    accent?: string
  }
  /** Resolved org-unit branding from settings' central asset store
   *  (src/data/orgBranding.ts). undefined/null or all-null fields ⇒ the
   *  honest NEUTRAL banner (no logo, neutral band, no hero). */
  branding?: OrgBrandingResolved | null
  stub?: Partial<WalletStubFields>
  /** Backing held ticket — the REAL QR token mints against it. Absent (e.g.
   *  fixture renders) ⇒ the QR block shows its honest unconfigured state. */
  ticketId?: string
}

// ---------------------------------------------------------------------------
// Derivations — REUSED from src/data/getReady.ts (no duplicates)
// ---------------------------------------------------------------------------

/** Issued tickets for the pager: derived, every step incl. system done.
 *  Settled PAST events (ends_at + grace behind now — the shared
 *  visibleGetReadyEvents boundary, BOARDROOM-2026-09-01-175306 item 2) are
 *  excluded, so the wallet pager and the Tickets nav badge (countIssued, the
 *  same filter) provably agree and a finished event's QR never presents as a
 *  live ticket. */
export const issuedWalletEvents = <T extends GetReadyEvent>(events: T[]): T[] =>
  visibleGetReadyEvents(events).filter(isIssued)

/** NOT-ISSUED-YET cards — derived from the same source of truth as Get ready:
 *  blocked (user still has `now` steps) or waiting (all yours done, system
 *  pending). POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): a CLOSED event's
 *  pending card is 'ended' — never "N things to do" (the server 409s those
 *  writes). ENDED = the composed eventEnded verdict (server close flag OR
 *  past the shared partition boundary — past-stubs hardening
 *  CMT-2026-09-03-025): with past to-dos zeroed at nowStepCount, keying on
 *  the raw flag alone would let a divergent past card read 'waiting'. */
export function pendingWalletReason(
  e: GetReadyEvent,
  t: TranslateFn = (k, p) => translate('en', k, p),
): { kind: 'blocked' | 'waiting' | 'ended'; text: string } {
  if (eventEnded(e)) {
    return { kind: 'ended', text: t('tickets.pending.ended') }
  }
  const todo = nowStepCount(e)
  if (todo > 0) {
    return {
      kind: 'blocked',
      text: todo === 1 ? t('tickets.pending.blocked.one') : t('tickets.pending.blocked.other', { n: todo }),
    }
  }
  // DC derivation: waitingText = first pending system step's meta,
  // `Automatic · ` stripped.
  const waitingText = (e.steps.find(s => s.system && s.state === 'soon')?.meta ?? '').replace('Automatic · ', '')
  return { kind: 'waiting', text: t('tickets.pending.waiting', { when: waitingText }) }
}

// ---------------------------------------------------------------------------
// Real QR (preserved strength — WU-3 prior art; the DC genQR fake never ships)
// ---------------------------------------------------------------------------

type QrLoadState = 'unconfigured' | 'loading' | 'ready' | 'not_ready' | 'unavailable'

function useEntryQr(ticketId?: string, purchaserOrderId?: string) {
  const [state, setState] = useState<QrLoadState>('unconfigured')
  const [dataUrl, setDataUrl] = useState<string | null>(null)

  useEffect(() => {
    const base = myApiBase()
    const session = loadSession()
    if (!ticketId || !base || !session) {
      setState('unconfigured')
      return
    }
    let cancelled = false
    setState('loading')
    ;(async () => {
      // ONE authed GET for the ticket's STABLE scan token — the bare-UUID QR
      // payload (BOARDROOM-2026-08-24-192147). The read stays SERVER-gated on
      // readiness, so a 409 not_ready refusal is an honest not-ready, never a
      // client-fabricated code.
      // DEFERRED (parent-signing) seats admitted to the buyer's wallet read
      // through the PURCHASER-scoped twin (my-api PR#125) — the holder route
      // 404s for the buyer. Own/held seats keep the holder path unchanged.
      let token: string | null = null
      try {
        ;({ token } = purchaserOrderId
          ? await fetchPurchaserScanToken(session.idToken, base, purchaserOrderId, ticketId)
          : await fetchScanToken(session.idToken, base, ticketId))
      } catch (e) {
        if (!cancelled) setState(e instanceof MyApiError && e.status === 409 ? 'not_ready' : 'unavailable')
        return
      }
      if (cancelled) return
      if (!token) {
        // Ready but no token on this environment — a service gap, not a
        // readiness gap; never render an invented code.
        setState('unavailable')
        return
      }
      // Centred QR, 116px. ECC 'Q' (~25% recovery) per the ratified design —
      // the payload is tiny (36 chars), so the denser modules cost nothing and
      // door-scan robustness wins.
      const url = await QRCode.toDataURL(token, { margin: 1, width: 116, errorCorrectionLevel: 'Q' })
      if (!cancelled) {
        setDataUrl(url)
        setState('ready')
      }
    })().catch(() => { if (!cancelled) setState('unavailable') })
    return () => { cancelled = true }
  }, [ticketId, purchaserOrderId])

  return { state, dataUrl }
}

function QrBlock({ ticketId, purchaserOrderId, t }: {
  ticketId?: string
  /** Present ⇔ this stub is a DEFERRED seat admitted to the buyer's wallet
   *  (qrAvailable) — the QR reads via the purchaser-scoped route (PR#125). */
  purchaserOrderId?: string
  t: TranslateFn
}) {
  const { state, dataUrl } = useEntryQr(ticketId, purchaserOrderId)
  if (state === 'ready' && dataUrl) {
    return <img src={dataUrl} alt={t('tickets.stub.qrAlt')} width={116} height={116} />
  }
  const message =
    state === 'loading' ? t('tickets.qr.loading')
      : state === 'not_ready' ? t('tickets.qr.notReady')
        : state === 'unavailable' ? t('tickets.qr.unavailable')
          : t('tickets.qr.unconfigured')
  return (
    <div
      role="status"
      data-wired={state === 'ready' ? 'true' : 'false'}
      style={{ width: 116, minHeight: 116, display: 'grid', placeItems: 'center', textAlign: 'center', fontSize: 11, lineHeight: 1.45, color: 'var(--tru-stub-sub)' }}
    >
      {message}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The classic stub — V1.9.1 measured transcription (banner → ADMIT ONE →
// participant → event/type → heat|bib|gate|date → perforation → QR stub)
// ---------------------------------------------------------------------------

/** Mono stub label — passLabel in the measured markup. */
const stubLabel: CSSProperties = {
  fontFamily: 'var(--tru-font-mono)', fontSize: 9, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '.2em', color: 'var(--tru-stub-sub)',
}

/** Perforation notch: a disc in the page-frame colour, half-clipped by the
 *  stub's overflow:hidden so it reads as a cut-out. Cosmetic only. */
const stubNotch: CSSProperties = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)',
  width: 18, height: 18, borderRadius: '50%', background: 'var(--tru-surface-app)',
}

function StubColumn({ testid, label, value }: { testid: string; label: string; value: string }) {
  return (
    <div data-testid={testid} style={{ minWidth: 0, borderInlineStart: '2px solid var(--stub-spine, rgba(0,0,0,.30))', paddingInlineStart: 8 }}>
      <div style={stubLabel}>{label}</div>
      <div className="mono" style={{ fontSize: 12, fontWeight: 700, marginBlockStart: 2, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  )
}

function WalletStub({ e, t }: { e: WalletEvent; t: TranslateFn }) {
  const stub = e.stub ?? {}
  // Banner theming is data per org unit (logo + accent + hero, nothing else).
  // Resolved branding first; the fixture-only organiser.accent seam second;
  // otherwise the honest NEUTRAL band — never a status hue, never a monogram.
  const accent = e.branding?.organiserAccent ?? e.organiser.accent ?? null
  const logoUrl = e.branding?.organiserLogoUrl ?? null
  const heroUrl = e.branding?.heroImageUrl ?? null
  const branded = !!(accent || logoUrl || heroUrl)
  return (
    <div
      id="tickets.stub"
      data-testid="tickets-stub"
      style={{
        position: 'relative', borderRadius: 'var(--tru-r-container)', overflow: 'hidden',
        color: 'var(--tru-stub-ink)',
        boxShadow: '0 18px 46px rgba(0,0,0,.45), 0 2px 0 rgba(255,255,255,.05)',
        // Ensure the notch discs never punch visible holes past the banner.
        background: 'var(--tru-stub-body)',
        // Column spines take the organiser accent when branded, neutral ink
        // when not (identity accent, never teal/amber).
        ['--stub-spine' as string]: accent ?? 'rgba(0,0,0,.30)',
      }}
    >
      {/* BANNER — organiser hero/accent/logo from the central asset store.
          Neutral fallback: flat neutral band, organiser name only. */}
      <div
        data-testid="tickets-stub-org"
        data-branded={branded ? 'true' : 'false'}
        style={{ position: 'relative', height: 76, background: accent ?? 'var(--tru-stub-banner-neutral)', overflow: 'hidden' }}
      >
        {heroUrl && (
          // BLUR-FILL + CONTAIN (BOARDROOM-2026-09-10-142321 item 1): the band
          // is a 76px strip but the org hero is often a poster-style asset with
          // baked-in text — a bare `cover` centre-crop shears that text (the
          // Duncan-live OnePulse card). Wallet-pass idiom instead: a blurred
          // cover backdrop fills the band edge-to-edge, the full asset renders
          // `contain` above it — nothing is ever cropped, any aspect fits. For
          // genuinely strip-shaped heroes `contain` fills the band anyway, so
          // well-fitted assets look exactly as before.
          <>
            <img
              data-testid="tickets-stub-hero-backdrop"
              src={heroUrl}
              alt=""
              aria-hidden
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(16px) saturate(1.1)', transform: 'scale(1.25)', opacity: 0.85 }}
            />
            <img
              data-testid="tickets-stub-hero"
              src={heroUrl}
              alt=""
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </>
        )}
        {accent && !heroUrl && (
          // Measured accent texture (repeating diagonal hairlines).
          <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(125deg, rgba(255,255,255,.14) 0 1px, transparent 1px 10px)' }} />
        )}
        {branded && (
          // Legibility gradient under the wordmark — only over real branding.
          <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 42%, rgba(0,0,0,.42) 100%)' }} />
        )}
        <div style={{ position: 'absolute', insetInline: 14, insetBlockEnd: 9, display: 'flex', alignItems: 'flex-end', gap: 9 }}>
          {logoUrl && (
            <img
              data-testid="tickets-stub-logo"
              src={logoUrl}
              alt=""
              style={{ flex: 'none', height: 22, maxWidth: 96, objectFit: 'contain' }}
            />
          )}
          <span
            data-testid="tickets-stub-org-name"
            style={{ flex: 1, minWidth: 0, color: '#fff', fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em', textShadow: '0 1px 6px rgba(0,0,0,.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >{e.organiser.name}</span>
        </div>
      </div>

      <div style={{ background: 'var(--tru-stub-body)', padding: '0 0 2px' }}>
        {/* ADMIT ONE strip */}
        <div
          data-testid="tickets-stub-admit"
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px 8px', borderBlockEnd: '1px solid var(--tru-stub-rule)' }}
        >
          <span style={{ ...stubLabel, flex: 1, minWidth: 0 }}>{t('tickets.stub.admitOne')}</span>
        </div>

        <div style={{ padding: '14px 14px 12px' }}>
          {/* Participant name LARGEST (README §Screens 3). */}
          <div style={{ ...stubLabel, marginBlockEnd: 4 }}>{t('tickets.stub.participant')}</div>
          <div data-testid="tickets-stub-name" style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.02, textTransform: 'uppercase', textWrap: 'pretty' }}>
            {stub.participantName || '—'}
          </div>
          <div aria-hidden style={{ borderBlockStart: '2px solid var(--tru-stub-ink)', margin: '11px 0 10px' }} />
          <div data-testid="tickets-stub-event" style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
            {e.name}
          </div>
          <div data-testid="tickets-stub-type" className="mono" style={{ fontSize: 10, color: 'var(--tru-stub-sub)', letterSpacing: '.09em', textTransform: 'uppercase', marginBlockStart: 3 }}>
            {stub.ticketType || '—'}
          </div>

          {/* HEAT | BIB | (GATE) | DATE columns, accent spines. Gate renders
              ONLY when the event has gates — no gate on the wire ⇒ no gate
              row, never an empty cell. */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${stub.gate ? 4 : 3}, minmax(0, 1fr))`, gap: 9, marginBlockStart: 13 }}>
            <StubColumn testid="tickets-stub-heat" label={t('tickets.stub.heat')} value={stub.heat || '—'} />
            {/* Bib is ALWAYS present in the layout (BOARDROOM-2026-08-13-223623):
                the field must exist even before allocation so a minted bib
                drops straight in; renders '—' until box-office allocates it. */}
            <StubColumn testid="tickets-stub-bib" label={t('tickets.stub.bib')} value={stub.bib || '—'} />
            {stub.gate ? <StubColumn testid="tickets-stub-gate" label={t('tickets.stub.gate')} value={stub.gate} /> : null}
            <StubColumn testid="tickets-stub-date" label={t('tickets.stub.date')} value={stub.date || '—'} />
          </div>
        </div>
      </div>

      {/* Perforation — dashed rule with edge notches. */}
      <div data-testid="tickets-stub-perforation" aria-hidden style={{ position: 'relative', height: 22, background: 'var(--tru-stub-body)' }}>
        <span data-testid="tickets-stub-notch" style={{ ...stubNotch, insetInlineStart: -9 }} />
        <span data-testid="tickets-stub-notch" style={{ ...stubNotch, insetInlineEnd: -9 }} />
        <div style={{ position: 'absolute', top: '50%', insetInline: 13, borderBlockStart: '2px dashed var(--tru-stub-perforation)' }} />
      </div>

      {/* QR stub below the perforation — REAL payload only (see QrBlock), one
          per screen. Serial/booking code = mono LTR island. */}
      <div style={{ background: 'var(--tru-stub-foot)', padding: '2px 14px 14px', textAlign: 'center' }}>
        <div data-testid="tickets-stub-qr" style={{ display: 'inline-block', background: '#fff', border: '1px solid rgba(0,0,0,.1)', borderRadius: 8, padding: 10, boxShadow: '0 1px 2px rgba(0,0,0,.07)' }}>
          {/* Deferred-admitted stubs read via the purchaser-scoped route
              (waiting.orderId); held seats keep the holder path (PR#125). */}
          <QrBlock ticketId={e.ticketId} purchaserOrderId={e.waiting?.orderId} t={t} />
        </div>
        {stub.serial && (
          <div
            data-testid="tickets-stub-serial"
            className="mono"
            style={{ fontSize: 9.5, letterSpacing: '.14em', color: 'var(--tru-stub-sub)', marginBlockStart: 9, direction: 'ltr', unicodeBidi: 'isolate' }}
          >{stub.serial}</div>
        )}
        <div className="mono" style={{ fontSize: 9, letterSpacing: '.2em', color: 'var(--tru-stub-scan-ink)', fontWeight: 700, marginBlockStart: 4 }}>
          {t('tickets.stub.scanAtGate')}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Wallet column (pager + stub + NOT ISSUED YET). Presentational: rendered by
// the live screen below; unit tests render it with the seed FIXTURE.
// ---------------------------------------------------------------------------

const pagerBtnStyle: CSSProperties = {
  minWidth: 44, minHeight: 44, borderRadius: 12,
  border: '1px solid var(--tru-border-12)', background: 'var(--tru-surface-3)',
  color: 'var(--tru-text-primary)', fontSize: 18, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
}

export function TicketsWallet({ events, initialPassId }: {
  events: WalletEvent[]
  /** Deep-link seam (`/tickets/:passId`): opens the pager on the issued
   *  ticket whose backing ticketId matches. */
  initialPassId?: string
}) {
  const navigate = useNavigate()
  const { t, dir } = useLocale()
  const issued = issuedWalletEvents(events)
  // Pending list applies the SAME past-event exclusion: a settled past event
  // gets no "ended" placeholder card here — its order stays viewable
  // read-only under Orders → Past events (BOARDROOM-2026-09-01-175306).
  // PARENT-SIGNING waiting cards (BOARDROOM-2026-09-02-150714 item 5) are
  // excluded outright: the ruling's "two places" are the Orders row + the
  // Get-ready card — the wallet holds the CALLER's tickets, and this seat is
  // in the parent's hands (a pending stub here would also nag "Get ready"
  // with steps the buyer cannot do).
  const pending = visibleGetReadyEvents(events).filter(e => !isIssued(e) && !e.waiting)

  const [rawIdx, setRawIdx] = useState(() => {
    const at = initialPassId ? issued.findIndex(e => e.ticketId === initialPassId) : -1
    return at >= 0 ? at : 0
  })
  // Wraps around (DC: ((idx % n) + n) % n).
  const n = issued.length
  const idx = n > 0 ? ((rawIdx % n) + n) % n : 0
  const pass = issued[idx]

  // Pager glyphs encode direction — swapped in code under RTL, never
  // CSS-mirrored (README §RTL: "ticket pager prev/next swap").
  const prevGlyph = dir === 'rtl' ? '›' : '‹'
  const nextGlyph = dir === 'rtl' ? '‹' : '›'

  return (
    <div style={{ maxWidth: 360, margin: '0 auto' }}>
      {pass ? (
        <>
          {/* Pager: ‹ / › (44px) around event name + mono `TICKET {i} of {n}` */}
          <div id="tickets.pager" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBlockEnd: 12 }}>
            <button data-testid="tickets-pager-prev" aria-label={t('tickets.pager.prev')} onClick={() => setRawIdx(i => i - 1)} style={pagerBtnStyle}>{prevGlyph}</button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{pass.name}</div>
              <div data-testid="tickets-pager-pos" className="mono" style={{ fontSize: 10, color: 'var(--tru-text-quaternary)', letterSpacing: '.14em' }}>
                {t('tickets.pager.pos', { i: idx + 1, n })}
              </div>
            </div>
            <button data-testid="tickets-pager-next" aria-label={t('tickets.pager.next')} onClick={() => setRawIdx(i => i + 1)} style={pagerBtnStyle}>{nextGlyph}</button>
          </div>

          {/* POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): a closed event's
              issued ticket stays in the wallet (history) but reads as ended —
              badge + dimmed stub. The QR block is left INTACT: check-in staff
              may legitimately scan late; my-api's resolve flags eventEnded
              instead of blocking. eventClosed is the SERVER's readiness flag —
              the FE never re-derives it from dates. */}
          {pass.eventClosed && (
            <div
              data-testid="tickets-ended-badge"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, marginBlockEnd: 10,
                borderRadius: 20, padding: '3px 9px', fontSize: 10.5, fontWeight: 800,
                background: 'rgba(255,255,255,.08)', color: 'var(--tru-text-muted)',
                border: '1px solid var(--tru-border-12)',
              }}
            >{t('tickets.ended.badge')}</div>
          )}
          {/* Exactly ONE stub (one QR) visible at a time. */}
          <div data-closed={pass.eventClosed ? 'true' : undefined} style={pass.eventClosed ? { opacity: 0.6 } : undefined}>
            {/* Keyed per HOLDING (child-holder slice): two same-event passes
                (guardian's own + a child's) must remount, not share state. */}
            <WalletStub key={`${pass.id}|${pass.ticketId ?? ''}`} e={pass} t={t} />
          </div>
          {/* No Apple/Google Wallet buttons: pass generation is not built
              server-side, and absent beats honestly-disabled (README
              §Screens 3). The in-app QR is the pass. */}
        </>
      ) : (
        // Honest empty wallet (Ruling A): no issued tickets ⇒ no stub, ever —
        // nothing demo-seeded.
        <p data-testid="tickets-empty" className="secondary" style={{ fontSize: 13 }}>
          {t('tickets.empty')}
        </p>
      )}

      {/* NOT ISSUED YET — derived from the same single source of truth as Get
          ready, "so the ticket a user is hunting for is never simply absent". */}
      {pending.length > 0 && (
        <div id="tickets.pending" data-testid="tickets-pending">
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.1em', color: 'var(--tru-text-muted)', margin: '26px 0 9px' }}>
            {t('tickets.pending.kicker')}
          </div>
          {pending.map(e => {
            const reason = pendingWalletReason(e, t)
            return (
              <div
                key={`${e.id}|${e.ticketId ?? ''}`}
                id={`tickets.pending.${e.id}`}
                data-testid="tickets-pending-card"
                data-event-id={e.id}
                data-closed={e.eventClosed ? 'true' : undefined}
                style={{
                  background: 'var(--tru-surface-3)', border: '1px dashed var(--tru-border-16)', borderRadius: 14, padding: 14, marginBlockEnd: 10,
                  // POST-EVENT CLOSE: dimmed archive read, same as the get-ready card.
                  ...(e.eventClosed ? { opacity: 0.6 } : {}),
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {e.organiser.name && (
                      // Accent is DATA per org unit; '' on the wire → NEUTRAL
                      // fallback, never a status hue (README §Status colours).
                      <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.11em', textTransform: 'uppercase', color: e.organiser.accentFg || 'var(--tru-text-tertiary)', marginBlockEnd: 3 }}>
                        {e.organiser.name}
                      </div>
                    )}
                    <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.2 }}>{e.name}</div>
                    {e.when && <div style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)' }}>{e.when}</div>}
                  </div>
                  {e.eventClosed && (
                    <span
                      data-testid="tickets-ended-badge"
                      style={{
                        flex: 'none', display: 'inline-flex', alignItems: 'center',
                        borderRadius: 20, padding: '3px 9px', fontSize: 10.5, fontWeight: 800,
                        background: 'rgba(255,255,255,.08)', color: 'var(--tru-text-muted)',
                        border: '1px solid var(--tru-border-12)',
                      }}
                    >{t('tickets.ended.badge')}</span>
                  )}
                </div>
                <div
                  data-testid="tickets-pending-reason"
                  style={{
                    fontSize: 11.5, lineHeight: 1.45, marginBlockStart: 7,
                    // blocked → amber; waiting/ended → muted (ended is calm,
                    // never amber urgency for something no longer doable).
                    ...(reason.kind === 'blocked'
                      ? { color: 'var(--tru-status-amber)', fontWeight: 700 }
                      : { color: 'var(--tru-text-tertiary)' }),
                  }}
                >
                  {reason.text}
                </div>
                {reason.kind === 'blocked' && (
                  <button
                    data-testid="tickets-pending-cta"
                    onClick={() => navigate('/get-ready')}
                    style={{ width: '100%', border: 0, borderRadius: 10, minHeight: 44, marginBlockStart: 11, background: 'var(--tru-status-teal)', color: 'var(--tru-accent-ink)', fontSize: 13, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' }}
                  >
                    {t('tickets.pending.cta')}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live screen — real held events, server-derived issuance (Rulings A + C)
// ---------------------------------------------------------------------------

/** Honest signed-out / expired-session guard (audit D23, same split as D7/D11):
 *  a signed-out user is NEVER told the build is unwired — they get a sign-in
 *  prompt + affordance, distinct from the no-API-base build gap (NotWired) and
 *  the fetch-failure "not responding" state. Mirrors GetReady's SignInNotice. */
function SignInNotice() {
  const { t } = useLocale()
  const [busy, setBusy] = useState(false)
  return (
    <div
      role="status"
      data-testid="tickets-signin"
      style={{
        border: '1px solid var(--tru-border-12)', borderRadius: 'var(--tru-r-container)',
        padding: 20, marginBlockStart: 8, maxWidth: 360,
        color: 'var(--tru-text-secondary)', background: 'var(--tru-surface-3)', fontSize: 13,
      }}
    >
      <p style={{ margin: '0 0 12px' }}>{t('tickets.signin.prompt')}</p>
      <button
        data-testid="tickets-signin-cta"
        onClick={() => { setBusy(true); void startSignIn().then(started => { if (!started) setBusy(false) }) }}
        disabled={busy}
        style={{
          minHeight: 44, borderRadius: 9, padding: '0 16px', border: 0,
          background: 'var(--tru-status-teal)', color: 'var(--tru-accent-ink)', fontFamily: 'inherit',
          fontSize: 13, fontWeight: 800, cursor: 'pointer',
        }}
      >
        {busy ? t('tickets.signin.busy') : t('tickets.signin.cta')}
      </button>
    </div>
  )
}

export function Tickets() {
  const { liveState, tickets, orders, eventById, orgBranding, requestOrgBranding } = useData()
  const { t } = useLocale()
  // The participant on every wallet ticket is the SIGNED-IN account. Prefer the
  // box-office assignee name, else the signed-in profile NAME (given+family from
  // the verified ID-token claims — real data, not fabricated), and NEVER the
  // email — the ticket must show a name, not "DUNCAN.FRANCIS@…" (CMT-142738).
  const selfProfileName = (() => {
    const c = loadSession()?.claims as Record<string, unknown> | undefined
    const parts = [c?.['given_name'], c?.['family_name']].filter(
      (s): s is string => typeof s === 'string' && s.trim() !== '',
    )
    return parts.join(' ').trim()
  })()
  // SAME live source as Get ready (Ruling C): server-derived readiness steps
  // per held event — the issued/pending split above derives from these rows.
  const { state, events, ticketByEvent } = useLiveGetReadyEvents()
  const { showPageHead } = useShellChrome()
  const { passId } = useParams()

  // INVITEE ORG-BRANDING (MY-2026-09-10-192731): holdings-sourced org ids are
  // not on the orders-driven prefetch set — ask the provider to resolve them.
  // Idempotent per id; non-UUIDs are ignored provider-side.
  useEffect(() => {
    for (const e of events) {
      if (e.orgUnitId && !eventById(e.id)?.organiser.id) requestOrgBranding(e.orgUnitId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events])

  const walletEvents: WalletEvent[] = events.map(e => {
    // The entry's OWN backing ticket (per-holding since the child-holder
    // slice) — the event-keyed map would cross a guardian's own seat with a
    // child seat in the same event. Fallback covers per-event legacy entries.
    const ticketId = e.ticketId ?? ticketByEvent[e.id]
    const tk = tickets.find(x => x.id === ticketId)
    const ev = eventById(e.id)
    // Banner branding is settings' resolved read — org-unit id ONLY when the
    // wire carries a REAL UUID (synthesised `org-{name}` fallback ids are
    // never sent; the endpoint 400s them). Box-office organiser id first;
    // else the holdings-carried org id (invitee org-branding carry,
    // MY-2026-09-10-192731 — an invitee has no box-office orders read).
    const orgUnitId = ev?.organiser.id || e.orgUnitId || undefined
    const branding = orgUnitId && isOrgUnitUuid(orgUnitId) ? orgBranding(orgUnitId) : undefined
    // Serial / booking code = the REAL order reference; omitted when unknown.
    const order = tk ? orders.find(o => o.id === tk.orderId) : undefined
    return {
      ...e,
      ticketId,
      branding,
      stub: {
        // A CHILD seat's wallet ticket names the CHILD — the holdings wire's
        // kind/holderName (child-holder slice) OR the local child-record
        // overlay (childKey — BOARDROOM-2026-09-02-150714 item 2: a child
        // ticket whose server holder is invitee/claimed still shows the
        // CHILD); every other holding is the signed-in account: box-office
        // assignee name when known, else the signed-in profile name. Email is
        // NOT a fallback (CMT-142738 — a ticket must show a name). RULING
        // GUARD: a child-subject seat NEVER borrows the guardian/account name
        // — an unnamed child renders the honest '—', not an adult's name.
        // DEFERRED seats (e.waiting — parent-signing, BOARDROOM-2026-09-02-
        // 152856) are child-subject too: the buyer-side stub names the CHILD.
        // GUARDIAN-CLAIMED seats (e.guardianClaim — S2 slice, BOARDROOM-2026-
        // 09-03-052111 / CMT-2026-09-03-020) likewise: the seat is the
        // CHILD's even though the wire holder is the parent's claim — the
        // stub names the child, never the signed-in parent.
        participantName: (e.holderKind === 'child' || e.childKey != null || e.waiting || e.guardianClaim)
          ? (e.holderName || '')
          : tk?.assignee?.name || selfProfileName || '',
        ticketType: tk?.typeName || '',
        // HEAT column shows the start TIME only, not the wave name. PREFER the
        // heat SELECTED in get-ready (my-api readiness selectedHeatTime,
        // CMT-121325); fall back to the box-office wave time; '' (→ em-dash)
        // only when neither source knows.
        heat: e.selectedHeatTime || tk?.wave?.time || '',
        // No live gate source yet ⇒ Gate column honestly omitted.
        gate: '',
        // DATE: prefer the box-office event's own display date; else map the
        // my-api readiness eventStartsAt (CMT-121325) so an issued ticket
        // shows the event date instead of '—'. '' (→ em-dash) only when
        // neither source knows the date.
        date: ev?.when || (e.eventStartsAt ? formatEventDate(e.eventStartsAt) : ''),
        // Minted bib — PREFER the /me/holdings carry (the holder-lane source
        // that reaches ASSIGNED holders, BOARDROOM-2026-09-11-131819); the
        // box-office payload stays as the purchaser-lane fallback; '—' until
        // settings mints it (BOARDROOM-2026-08-13-223623).
        bib: formatBib(e.bib ?? tk?.bib) ?? '',
        serial: order?.ref || '',
      },
    }
  })

  return (
    <div id="tickets.root">
      {/* Desktop-only page head */}
      {showPageHead && (
        <div id="tickets.head" data-testid="tickets-head">
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 4px' }}>{t('tickets.head.title')}</h1>
          <p style={{ fontSize: 13, color: 'var(--tru-text-secondary)', margin: '0 0 18px' }}>
            {t('tickets.head.sub')}
          </p>
        </div>
      )}
      {state === 'signed-out' ? (
        // Honest sign-out copy — checked FIRST so a signed-out / expired user
        // is never shown the my-api NotWired "not connected in this build"
        // panel (audit D23); a 401/403 mid-session lands here too.
        <SignInNotice />
      ) : liveState !== 'ready' ? (
        <LiveDataNotice liveState={liveState} />
      ) : state === 'unconfigured' ? (
        <NotWired service="The ticket issuance/readiness service (my-api)" />
      ) : state === 'loading' ? (
        <p className="secondary" style={{ fontSize: 13 }} role="status">{t('tickets.loading')}</p>
      ) : state === 'unavailable' ? (
        <p className="secondary" role="alert" style={{ fontSize: 13 }}>
          {t('tickets.unavailable')}
        </p>
      ) : (
        <TicketsWallet events={walletEvents} initialPassId={passId} />
      )}
    </div>
  )
}
